'use es6';

import { createSelector } from 'reselect';
import { List } from 'immutable';
import { getLessons } from './lessonSelectors';
import { getContent } from './contentSelectors';
import { TRACK_COMPLETE_URL_FRAGMENT } from '../constants/Navigation';
import { getTrackExamPageUrl } from '../utils/RoutingUtils';
export const getTracks = state => state.tracks.get('tracksById');
export const getTrack = createSelector(getTracks, (state, props) => props.trackId, (tracks, trackId) => tracks.get(trackId) || tracks.get(`${trackId}`));
export const getTracksAreRequesting = state => {
  return state && state.tracks && (state.tracks.get('isRequesting') || state.tracks.get('isRequestingPartner') || state.tracks.get('isRequestingSearch') || !state.tracks.get('lastValidResult') && !state.tracks.get('hasError'));
};
export const getTrackByIdIsLoading = state => {
  return state.tracks.get('isRequestingTrackById');
};
export const trackIsLoading = createSelector(getTrack, track => track ? track.get('isRequesting', true) : true);
export const canOperateOnTrack = createSelector(getTrack, trackIsLoading, (track, isLoading) => !isLoading && !track.get('hasError'));
export const getLessonsForTrack = createSelector(getTrack, getLessons, (track, lessonsById) => track && lessonsById.size && track.get('lessonIds') && track.get('lessonIds').map(lessonId => lessonsById.get(lessonId) || lessonsById.get(`${lessonId}`)) || []);
export const getContentsForLessons = createSelector(getLessonsForTrack, getContent, canOperateOnTrack, (lessons, content, trackIsReady) => {
  if (!trackIsReady || !lessons || !content.size) {
    return List();
  }
  return lessons.flatMap(lesson => lesson && lesson.get('contentIds').map(contentId => content.get(contentId)));
});
export const getTrackName = createSelector(getTrack, track => {
  if (!track) {
    return '';
  }
  return track.get('name', '');
});
export const getTrackLanguage = createSelector(getTrack, track => {
  if (!track) {
    return '';
  }
  return track.get('language', '');
});
export const getTrackExamId = createSelector(getTrack, canOperateOnTrack, (track, trackIsReady) => {
  if (!trackIsReady) {
    return undefined;
  }
  return track.get('examId');
});
export const getTrackExam = createSelector(getTrackExamId, getContent, (examId, contentById) => {
  if (!examId) {
    return undefined;
  }
  return contentById.get(examId);
});
export const getTimeForTrackInSeconds = createSelector(getTrack, track => {
  return track && track.get('duration');
});
export const getFirstContentForTrackUrl = createSelector(getTrack, getLessonsForTrack, canOperateOnTrack, (track, lessons, trackIsReady) => {
  if (!trackIsReady) {
    return undefined;
  }
  const firstLessonId = track.get('lessonIds').first();
  const firstLesson = lessons.find(thisLesson => thisLesson && thisLesson.get('id') === firstLessonId);
  if (!firstLesson) {
    return undefined;
  }
  const firstLessonContentId = firstLesson.get('contentIds').first();
  return `/tracks/${track.get('id')}/${firstLessonId}/${firstLessonContentId}`;
});
export const getIsLastLessonInTrack = createSelector(getTrack, (_, props) => props.lessonId, (track, lessonId) => track && track.get('lessonIds', List()).last() === lessonId);
export const getNextContentUrl = createSelector(getTrack, canOperateOnTrack, getLessonsForTrack, getIsLastLessonInTrack, getTrackExam, (_, props) => props.lessonId, (_, props) => props.contentId, (track, trackIsReady, lessons, isLastLesson, exam, lessonId, contentId) => {
  if (!trackIsReady || !lessonId) {
    return undefined;
  }
  const indexOfLessonInTrackLessons = track.get('lessonIds').indexOf(lessonId);
  const lesson = lessons.get(indexOfLessonInTrackLessons);
  if (!lesson) {
    return undefined;
  }
  const indexOfContentInLesson = lesson.get('contentIds').indexOf(contentId);
  const contentIsLastInLesson = indexOfContentInLesson >= lesson.get('contentIds').count() - 1;
  if (contentIsLastInLesson) {
    if (isLastLesson) {
      if (exam && !exam.get('completed')) {
        return getTrackExamPageUrl(track.get('id'));
      }
      return `/tracks/${track.get('id')}/${TRACK_COMPLETE_URL_FRAGMENT}`;
    }
    const nextLesson = lessons.get(indexOfLessonInTrackLessons + 1);
    return nextLesson && `/tracks/${track.get('id')}/${nextLesson.get('id')}/${nextLesson.getIn(['contentIds', 0])}`;
  }
  return `/tracks/${track.get('id')}/${lesson.get('id')}/${lesson.getIn(['contentIds', indexOfContentInLesson + 1])}`;
});